import { useMemo } from 'react'
import { getFlavorsByCategory, type Flavor } from '../data'
import { getFlavorEmoji, getCategoryColor } from '../data/flavorMeta'
import { getFlavorImage } from '../data/flavorImages'

interface FlavorGridProps {
  search: string
  view: 'table' | 'list'
  onSelect: (flavor: Flavor) => void
}

/** Склонение: 1 сочетание, 2 сочетания, 5 сочетаний. */
const pairingsLabel = (n: number) => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return `${n} сочетание`
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} сочетания`
  return `${n} сочетаний`
}

/** Мини-карточка вкуса: картинка (или эмодзи-заглушка) + имя + число сочетаний. */
const ProductCardMini = ({
  flavor,
  onSelect,
}: {
  flavor: Flavor
  onSelect: (f: Flavor) => void
}) => {
  const image = getFlavorImage(flavor.slug)
  return (
    <button
      type="button"
      className="pf-card"
      onClick={() => onSelect(flavor)}
    >
      <span className="pf-card__img">
        {image ? (
          <img src={image} alt="" loading="lazy" />
        ) : (
          <span
            className="pf-card__img-fallback"
            style={{ background: `${getCategoryColor(flavor.category)}33` }}
            aria-hidden="true"
          >
            {getFlavorEmoji(flavor.slug)}
          </span>
        )}
      </span>
      <span className="pf-card__info">
        <span className="pf-card__name">{flavor.name}</span>
        <span className="pf-card__count">
          {pairingsLabel(flavor.pairings.length)}
        </span>
      </span>
    </button>
  )
}

/**
 * Крупная карточка вкуса для режима «Список» (product-card-md):
 * картинка сверху + имя + тег категории с цветной точкой.
 */
const FlavorCardMd = ({
  flavor,
  onSelect,
}: {
  flavor: Flavor
  onSelect: (f: Flavor) => void
}) => {
  const image = getFlavorImage(flavor.slug)
  return (
    <button type="button" className="pf-md" onClick={() => onSelect(flavor)}>
      <span className="pf-md__img">
        {image ? (
          <img src={image} alt="" loading="lazy" />
        ) : (
          <span
            className="pf-md__img-fallback"
            style={{ background: `${getCategoryColor(flavor.category)}33` }}
            aria-hidden="true"
          >
            {getFlavorEmoji(flavor.slug)}
          </span>
        )}
      </span>
      <span className="pf-md__content">
        <span className="pf-md__name">{flavor.name}</span>
        <span className="pf-md__taste">
          <span
            className="pf-md__dot"
            style={{ background: getCategoryColor(flavor.category) }}
            aria-hidden="true"
          />
          <span className="pf-md__cat">{flavor.category}</span>
        </span>
      </span>
    </button>
  )
}

/**
 * Вкусы, сгруппированные по книжным категориям. Режим «Таблица» — карточки
 * в две колонки, «Список» — строки. При активном поиске категории
 * схлопываются в список подходящих вкусов.
 */
const FlavorGrid = ({ search, view, onSelect }: FlavorGridProps) => {
  const groups = useMemo(() => getFlavorsByCategory(), [])

  const query = search.trim().toLowerCase()

  const filteredGroups = useMemo(() => {
    if (!query) return groups
    return groups
      .map(g => ({
        ...g,
        flavors: g.flavors.filter(f => f.name.toLowerCase().includes(query)),
      }))
      .filter(g => g.flavors.length > 0)
  }, [groups, query])

  if (filteredGroups.length === 0) {
    return (
      <p className="flavors-empty">
        По запросу «{search}» вкусов не нашлось
      </p>
    )
  }

  return (
    <div className="flavors-groups">
      {filteredGroups.map(group => (
        <section key={group.category} className="flavors-group">
          <h2 className="flavors-group__title">
            <span
              className="flavors-group__dot"
              style={{ background: getCategoryColor(group.category) }}
              aria-hidden="true"
            />
            {group.category}
          </h2>
          {view === 'table' ? (
            <div className="flavors-grid">
              {group.flavors.map(flavor => (
                <ProductCardMini
                  key={flavor.slug}
                  flavor={flavor}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ) : (
            <div className="flavors-md-grid">
              {group.flavors.map(flavor => (
                <FlavorCardMd
                  key={flavor.slug}
                  flavor={flavor}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  )
}

export default FlavorGrid
